import axios from 'axios';
import { clients, gatewayClients } from './clients';
/** Default name of the id token header (matches the legacy `TFPL_ID_TOKEN`). */
export const TFPL_ID_TOKEN_HEADER = 'tfpl-id-token';
const isUnauthorized = (error) => axios.isAxiosError(error) && error.response?.status === 401;
/** Live browser options — interceptors read these at call time, so re-configuration updates behavior without stacking handlers. */
let browserOptions;
/** Gateway clients whose interceptors are already registered (registration is one-time). */
const interceptedClients = new Set();
/**
 * Frontend client configuration: a single baseURL (gateway) + auth interceptor (id token)
 * + 401 handler. Interceptors are registered **on the instance** of each client
 * (Hey API has its own axios instance, so global interceptors would not apply).
 *
 * Idempotent: interceptors are registered exactly once per client; repeated calls (HMR,
 * re-login, tenant switch) only refresh the live options, never stack new handlers.
 */
export const configureBrowserClients = (options) => {
    browserOptions = options;
    for (const name of Object.keys(gatewayClients)) {
        const client = gatewayClients[name];
        // throwOnError: true → SDK throws on non-2xx (matches the legacy ApiError behavior)
        client.setConfig({ baseURL: options.baseUrl, throwOnError: true });
        if (interceptedClients.has(name))
            continue;
        interceptedClients.add(name);
        client.instance.interceptors.request.use(async (config) => {
            const opts = browserOptions;
            const header = opts.tokenHeader ?? TFPL_ID_TOKEN_HEADER;
            // On a post-401 retry the token is force-refreshed (see response interceptor).
            const forceRefresh = config.__forceTokenRefresh === true;
            config.headers.set(header, await opts.getIdToken(forceRefresh));
            return config;
        });
        client.instance.interceptors.response.use((response) => response, async (error) => {
            const opts = browserOptions;
            if (opts && isUnauthorized(error) && axios.isAxiosError(error) && error.config) {
                const config = error.config;
                // Self-heal step 1: a stale/expired id token is the common cause of a
                // 401. Force-refresh the token and retry the request exactly once (the
                // flag both guards against loops and tells the request interceptor to
                // force-refresh). Transient failures recover invisibly to the user.
                if (!config.__forceTokenRefresh) {
                    config.__forceTokenRefresh = true;
                    return client.instance.request(config);
                }
                // Self-heal step 2: still 401 after a fresh token → the session is
                // genuinely dead. Hand off to the caller's recovery (e.g. logout +
                // redirect to login) instead of surfacing an unrecoverable error.
                await opts.onUnauthorized?.();
            }
            throw error;
        });
    }
};
/**
 * Backend (server-to-server) configuration: each service has its own direct host,
 * without a Firebase token / without 401 logout (internal network trust).
 */
export const configureNodeClients = (options) => {
    for (const [name, baseURL] of Object.entries(options.baseUrls)) {
        if (baseURL)
            clients[name].setConfig({ baseURL, throwOnError: true });
    }
};
